import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import {
  CANDIDATE_COMMENT_MAX,
  rejectionPresenters,
  rejectionReasonSchema,
  type RejectionReason,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  preview: {
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

export interface RejectDialogProps {
  readonly open: boolean;
  readonly itemKey: string;
  readonly heading: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (decision: { reason: RejectionReason; note: string; candidateComment: string }) => void;
}

/**
 * Sending work back.
 *
 * The reason is a closed code, chosen from the registry rather than typed, because it does
 * two jobs free text cannot: it selects the sentence the candidate reads and it selects the
 * email fragment. The note is HR's own words and stays internal.
 *
 * The candidate's sentence is shown to the reviewer BEFORE they send it. A reviewer picking
 * "Name does not match" should see that the person will be told to ring the office, because
 * that is the actual consequence of the click.
 */
export function RejectDialog({ open, itemKey, heading, busy, onCancel, onConfirm }: RejectDialogProps) {
  const styles = useStyles();
  const [reason, setReason] = useState<RejectionReason | null>(null);
  const [note, setNote] = useState('');
  const [candidateComment, setCandidateComment] = useState('');

  useEffect(() => {
    setReason(null);
    setNote('');
    setCandidateComment('');
  }, [open, itemKey]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => (data.open ? null : onCancel())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Send back: {heading}</DialogTitle>
          <DialogContent className={styles.body}>
            <Field label="Why is it coming back?" required>
              <RadioGroup
                value={reason ?? ''}
                onChange={(_, data) => {
                  const parsed = rejectionReasonSchema.safeParse(data.value);
                  if (parsed.success) setReason(parsed.data);
                }}
              >
                {rejectionReasonSchema.options.map((option) => (
                  <Radio key={option} value={option} label={rejectionPresenters[option].label} />
                ))}
              </RadioGroup>
            </Field>

            {reason === null ? null : (
              <div className={styles.preview}>
                <Text size={200} weight="semibold">
                  What they will read
                </Text>
                <br />
                <Text size={200}>{rejectionPresenters[reason].personFacing}</Text>
              </div>
            )}

            <Field
              label="Message to the candidate (optional)"
              hint={`They will read this, under the reason above, and it is in the email we send them. Do not include personal or health details. ${candidateComment.length} of ${CANDIDATE_COMMENT_MAX} characters.`}
            >
              <Textarea value={candidateComment} maxLength={CANDIDATE_COMMENT_MAX} onChange={(_, d) => setCandidateComment(d.value)} />
            </Field>

            <Field label="Note for our records" hint="Only our office sees this.">
              <Textarea value={note} maxLength={1000} onChange={(_, d) => setNote(d.value)} />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              // No reason means the candidate gets their work back with no explanation,
              // which produces the phone call this surface exists to prevent.
              disabled={reason === null || busy}
              onClick={() => reason !== null && onConfirm({ reason, note, candidateComment })}
            >
              {busy ? 'Sending back…' : 'Send it back'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
